import { QueryDataSource } from '@softapps/core';
import {
  ApiDataAccess,
  ApiResourceLocation,
  HttpApiQueryRepository,
  interpolate,
} from '@softline/core';
import { Query, QueryResult } from '@softline/core';
import { getDocumentQueryTypePrefix } from '../../helper/document-query/document-query-prefix.function';
import { SOFTLINE_API_DOCUMENT_QUERY } from '../../sales.api';
import { AbortableApiDataAccess } from '@softline/core';
import { DocumentIconsService } from '../../services/document-icons.service';
import { DocumentQueryRow } from '../../types/document-query';

const ICON_SUPPORTED_TYPES: ReadonlySet<string> = new Set([
  'SALES_ORDER',
  'OFFER',
  'BLANKET_ORDER',
  'DELIVERY_NOTE',
  'MATERIAL_DELIVERY_NOTE',
  'CONSOLIDATED_MEASUREMENT_REPORT',
  'COMMISSION_NOTE',
  'WORK_ORDER',
  'INVOICE',
  'CASH_SALE_INVOICE',
  'CREDIT_INVOICE',
]);

export class DocumentGridDataSource
  extends HttpApiQueryRepository<DocumentQueryRow>
  implements QueryDataSource<DocumentQueryRow>
{
  type = 'query' as const;

  constructor(
    dataAccess: ApiDataAccess,
    private readonly documentType: string,
    private readonly iconsService?: DocumentIconsService
  ) {
    super(dataAccess, interpolate(SOFTLINE_API_DOCUMENT_QUERY, { documentType }));
  }

  override async load(params: {
    query: Query<DocumentQueryRow>;
  }): Promise<QueryResult<DocumentQueryRow>> {
    const result = await super.load(params);
    if (this.iconsService && ICON_SUPPORTED_TYPES.has(this.documentType)) {
      const prefix = getDocumentQueryTypePrefix(this.documentType);
      const ids = (result?.data ?? [])
        .map(
          (row) =>
            (row as unknown as Record<string, { id?: unknown } | undefined>)[prefix]?.id
        )
        .filter((id): id is string => typeof id === 'string' && id.length > 0);
      if (ids.length > 0) {
        void this.iconsService.loadFor(ids);
      }
    }
    return result;
  }

  loadAbortable(
    params: { query: Query<DocumentQueryRow> },
    signal?: AbortSignal
  ): Promise<{ data: DocumentQueryRow[] }> {
    if (!signal || !(this.dataAccess instanceof AbortableApiDataAccess)) {
      return this.load(params) as Promise<{ data: DocumentQueryRow[] }>;
    }
    const location: ApiResourceLocation = {
      path: Array.isArray(this.path) ? this.path : [this.path],
    };
    return this.dataAccess.getAbortable<{ data: DocumentQueryRow[] }>(
      location,
      params.query,
      signal
    );
  }
}
